import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { CandidateListing, ReviewUnit } from "../../../api/content/curationApi";
import { getItemVersion } from "../../../api/content/itemsApi";
import type { Review } from "../../../api/content/reviewDecoders";
import ErrorNotice from "../../../errors/ErrorNotice";
import { useSubmission } from "../../../shared/state/useSubmission";
import AppButton from "../../../ui/buttons/AppButton";
import { useMemberNames } from "../../threads/data/useMemberNames";
import { downloadText, proposalFileName, proposalMarkdown } from "./proposalMarkdown";

const RDF_KINDS = new Set(["ontology_module", "shapes"]);

/** Download the proposal (v1's download menu): its explanation and changes as Markdown,
 * and each changed ontology module or shapes graph as Turtle at the head shown. */
export default function DownloadMenu({ review, units, candidates }: { review: Review; units: readonly ReviewUnit[]; candidates: CandidateListing | null }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const submission = useSubmission();
  const people = useMemberNames(review.changeSet.projectId);
  const rdfItems = review.items.filter((item) => RDF_KINDS.has(item.kind) && item.newReference !== null);
  return (
    <div className="app-dropdown-anchor relative">
      {/* Bordered, so it reads as the action it is, not as a bold heading (usability round 5). */}
      <AppButton
        size="sm"
        variant="secondary"
        aria-expanded={open}
        onClick={() => {
          setOpen(!open);
        }}
      >
        {t("reviews.curation.download.open")}
      </AppButton>
      {open ? (
        <div className="app-dropdown-panel app-dropdown-popover app-dropdown-popover-right z-20 w-72 space-y-1 p-2">
          <AppButton
            size="sm"
            variant="ghost"
            fullWidth={true}
            onClick={() => {
              downloadText(proposalFileName(review.changeSet.title, "md"), proposalMarkdown(review, units, candidates, people.nameOf), "text/markdown");
              setOpen(false);
            }}
          >
            {t("reviews.curation.download.markdown")}
          </AppButton>
          {rdfItems.map((item) => (
            <AppButton
              key={item.itemId}
              size="sm"
              variant="ghost"
              fullWidth={true}
              onClick={() => {
                submission.submit(async () => {
                  const version = await getItemVersion(item.newReference ?? "");
                  downloadText(proposalFileName(item.name, "ttl"), version.text, "text/turtle");
                  setOpen(false);
                });
              }}
            >
              {t("reviews.curation.download.turtle", { name: item.name })}
            </AppButton>
          ))}
          {submission.state.status === "failed" ? <ErrorNotice error={submission.state.error} compact={true} /> : null}
        </div>
      ) : null}
    </div>
  );
}
